import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import api, { gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Sparkles, Trash2, ArrowRight, Check, X, GripVertical, Settings, ArrowUp, ArrowDown, ExternalLink, Mic, Square, Upload, Clock, AlertTriangle, ListChecks, Flame, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/dialogs";

const OPEN_TONES = ["text-slate-600 border-slate-300", "text-blue-700 border-blue-300", "text-cass-teal border-teal-300", "text-amber-700 border-amber-300", "text-violet-700 border-violet-300", "text-cyan-700 border-cyan-300"];
const CARD_TONES = ["border-l-slate-400", "border-l-blue-400", "border-l-teal-400", "border-l-amber-400", "border-l-violet-400", "border-l-cyan-400"];
const SALES_RAMP = ["#F6D5FB", "#EFB0F7", "#E084EE", "#CB4FDD", "#AE28C2", "#8A1C9B"];
const TEMPS = {
  cold: { label: "Cold", pill: "bg-sky-100 text-sky-700 border-sky-200", flame: "text-sky-500" },
  warm: { label: "Warm", pill: "bg-amber-100 text-amber-700 border-amber-200", flame: "text-amber-500" },
  hot: { label: "Hot", pill: "bg-red-100 text-red-700 border-red-200", flame: "text-red-500" },
};

const TURNOVER_BANDS = ["< £85k", "£85k–£250k", "£250k–£500k", "£500k–£1m", "£1m–£5m", "£5m+"];
const BUSINESS_TYPES = [["limited_company", "Limited company"], ["sole_trader", "Sole trader"], ["partnership", "Partnership"], ["llp", "LLP"], ["charity", "Charity"], ["other", "Other"]];
const MEETING_TYPES = ["In person", "Teams", "Phone", "Video"];
const DISCOVERY_STATUSES = ["Booked", "Completed", "Cancelled", "No Show", "Rearranged"];
const URGENCY = ["ASAP", "1–3 months", "3–6 months", "Just exploring"];

const initials = (n) => (n || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const NONE = "__none__";
const fmtDT = (iso) => { if (!iso) return ""; const d = new Date(iso); return isNaN(d.getTime()) ? iso : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" }); };
const isOverdue = (d) => { if (!d) return false; const dt = new Date(d); return !isNaN(dt.getTime()) && dt < new Date(new Date().toDateString()); };

function AudioUploadButton({ onText, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("audio", file, file.name || "audio");
      const { data } = await api.post("/pipeline/transcribe", fd);
      if (data.text) onText(data.text); else toast.error("Nothing was transcribed");
    } catch (err) { toast.error(err.response?.data?.detail || "Transcription failed"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="audio/*,.mp3,.m4a,.wav,.webm,.mp4" className="hidden" onChange={onFile} data-testid={`${testid}-input`} />
      <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={testid} title="Upload an audio file">
        <Upload className="w-3.5 h-3.5 mr-1" />{busy ? "…" : "Upload"}
      </Button>
    </>
  );
}

function MicButton({ onText, testid }) {
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const chunks = useRef([]);
  const toggle = async () => {
    if (rec) { rec.stop(); return; }
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Microphone not supported in this browser"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRec(null); setBusy(true);
        try {
          const blob = new Blob(chunks.current, { type: "audio/webm" });
          const fd = new FormData(); fd.append("audio", blob, "note.webm");
          const { data } = await api.post("/pipeline/transcribe", fd);
          if (data.text) onText(data.text); else toast.error("Nothing was transcribed");
        } catch (e) { toast.error(e.response?.data?.detail || "Transcription failed"); }
        finally { setBusy(false); }
      };
      mr.start(); setRec(mr);
    } catch (e) { toast.error("Microphone permission denied"); }
  };
  return (
    <Button type="button" size="sm" variant="outline" onClick={toggle} disabled={busy} data-testid={testid}
      className={rec ? "text-red-600 border-red-300 animate-pulse" : ""} title="Dictate a note">
      {busy ? "…" : rec ? <><Square className="w-3.5 h-3.5 mr-1" />Stop</> : <><Mic className="w-3.5 h-3.5 mr-1" />Dictate</>}
    </Button>
  );
}

function LeadCard({ lead, onOpen, tone, selectMode, selected, onToggleSelect }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.id, disabled: selectMode });
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50 } : undefined;
  const overdue = isOverdue(lead.next_action_date);
  return (
    <div ref={setNodeRef} style={style}
      className={`bg-white rounded-xl border border-border border-l-4 ${tone} p-3 shadow-sm ${isDragging ? "opacity-70 ring-2 ring-cass-teal" : ""} ${selected ? "ring-2 ring-fuchsia-500" : ""}`}
      data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-start gap-2">
        {selectMode ? (
          <Checkbox checked={selected} onCheckedChange={() => onToggleSelect(lead.id)} className="mt-0.5" data-testid={`lead-select-${lead.id}`} />
        ) : (
          <button {...attributes} {...listeners} className="mt-0.5 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing" data-testid={`lead-drag-${lead.id}`}>
            <GripVertical className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => (selectMode ? onToggleSelect(lead.id) : onOpen(lead))} className="flex-1 min-w-0 text-left" data-testid={`lead-open-${lead.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="font-semibold text-cass-navy leading-tight min-w-0 truncate">{lead.company}</div>
            {TEMPS[lead.temperature || "warm"] && (
              <span className={`inline-flex items-center gap-0.5 shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${TEMPS[lead.temperature || "warm"].pill}`} title={`${TEMPS[lead.temperature || "warm"].label} lead`} data-testid={`lead-temp-${lead.id}`}>
                <Flame className="w-3 h-3" />{TEMPS[lead.temperature || "warm"].label}
              </span>
            )}
          </div>
          {Number(lead.expected_fee) > 0 && <div className="text-cass-teal font-medium tabular text-sm mt-0.5">{gbp(lead.expected_fee)}/mo</div>}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-cass-slate">
            {lead.lead_source && <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" />{lead.lead_source}</span>}
            {lead.proposal_id && <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200 text-[10px]">Proposal</Badge>}
          </div>
          {lead.next_action && (
            <div className={`flex items-center gap-1 mt-2 text-xs min-w-0 ${overdue ? "text-red-600 font-medium" : "text-cass-slate"}`} data-testid={`lead-next-action-${lead.id}`}>
              {overdue ? <AlertTriangle className="w-3 h-3 shrink-0" /> : <Clock className="w-3 h-3 shrink-0" />}
              <span className="truncate min-w-0">{lead.next_action}{lead.next_action_date ? ` · ${lead.next_action_date}` : ""}</span>
            </div>
          )}
          <div className="flex items-center justify-between mt-2">
            <span className="w-6 h-6 rounded-full bg-cass-navy text-white text-[10px] grid place-items-center font-semibold" title={lead.owner_name}>{initials(lead.owner_name)}</span>
            {lead.stage === "won" && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">✓ {lead.win_reason}</Badge>}
            {lead.stage === "lost" && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]">✗ {lead.loss_reason}</Badge>}
          </div>
        </button>
      </div>
    </div>
  );
}

function Column({ col, leads, onOpen, isFocus, selectMode, selected, onToggleSelect, onSelectAllStage, onEmptyStage, isAdmin, stageTotal }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key });
  const total = leads.reduce((a, l) => a + (Number(l.expected_fee) || 0), 0);
  const showTotal = col.key !== "won" && col.key !== "lost";
  return (
    <div ref={setNodeRef} className={`flex-1 min-w-[240px] rounded-xl p-2 transition-colors ${isOver ? "bg-cass-teal/10" : "bg-slate-50"} ${isFocus ? "ring-2 ring-fuchsia-400 ring-offset-2" : ""}`} data-testid={`pipeline-column-${col.key}`}>
      <div className="-mx-2 -mt-2 mb-2">
        <div className={`h-11 flex items-center justify-between pl-4 pr-5 ${col.headLight ? "text-cass-navy" : "text-white"}`}
          style={{ background: col.head, clipPath: "polygon(0 0,calc(100% - 12px) 0,100% 50%,calc(100% - 12px) 100%,0 100%)" }}>
          <span className="font-alt font-bold uppercase text-xs tracking-wider">{col.label} <span className="opacity-70" data-testid={`pipeline-col-count-${col.key}`}>({leads.length})</span></span>
          {showTotal && <span className="text-[11px] tabular opacity-90" data-testid={`pipeline-col-total-${col.key}`}>{gbp(total)}/mo</span>}
        </div>
      </div>
      {selectMode && isAdmin && (
        <div className="flex items-center justify-between px-1 mb-2">
          <button onClick={() => onSelectAllStage(col.key)} disabled={!leads.length} className="text-[11px] font-medium text-cass-slate hover:text-fuchsia-600 inline-flex items-center gap-1 disabled:opacity-40" data-testid={`pipeline-select-all-${col.key}`}>
            <ListChecks className="w-3.5 h-3.5" />Select all
          </button>
          <button onClick={() => onEmptyStage(col)} disabled={!stageTotal} className="text-[11px] font-medium text-red-500 hover:text-red-700 inline-flex items-center gap-1 disabled:opacity-40" data-testid={`pipeline-empty-stage-${col.key}`}>
            <Trash2 className="w-3.5 h-3.5" />Empty stage
          </button>
        </div>
      )}
      <div className="space-y-2 min-h-[120px]">
        {leads.map((l) => <LeadCard key={l.id} lead={l} onOpen={onOpen} tone={col.cardTone} selectMode={selectMode} selected={selected.has(l.id)} onToggleSelect={onToggleSelect} />)}
      </div>
    </div>
  );
}

const emptyForm = {
  company: "", client_id: "", contact_name: "", email: "", phone: "", website: "",
  company_number: "", business_type: "", expected_fee: "", close_date: "",
  sales_source: "", lead_source: "", referrer: "", referrer_client_id: "",
  owner_id: "", owner_name: "", stage: "new_enquiry", enquiry_date: "", temperature: "warm",
  next_action: "", next_action_date: "",
  reason_for_enquiry: "", current_accountant: "", current_software: "",
  turnover_band: "", employee_count: "", services_interested: [], discovery: {}, note: "",
};

export default function Pipeline() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [meta, setMeta] = useState({ sales_sources: [], win_reasons: [], loss_reasons: [], lead_sources: [], discovery_outcomes: [] });
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [users, setUsers] = useState([]);
  const [fOwner, setFOwner] = useState(NONE);
  const [fSource, setFSource] = useState(NONE);
  const [q, setQ] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [reason, setReason] = useState({ open: false, id: null, stage: null, value: "", detail: "" });
  const [tpMgmt, setTpMgmt] = useState({ open: false, rows: [] });
  const [focusStage, setFocusStage] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [lastDelete, setLastDelete] = useState(null);
  const [matchKw, setMatchKw] = useState("");
  const isAdmin = user?.role === "super_admin";
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      openNew();
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    const sp = searchParams.get("stage");
    if (!sp) return;
    setFocusStage(sp);
    const t = setTimeout(() => document.querySelector(`[data-testid="pipeline-column-${sp}"]`)?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" }), 400);
    const clear = setTimeout(() => setFocusStage(null), 2600);
    return () => { clearTimeout(t); clearTimeout(clear); };
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const [noteInput, setNoteInput] = useState("");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = () => {
    api.get("/pipeline/leads").then((r) => setLeads(r.data || []));
    api.get("/pipeline/meta").then((r) => setMeta(r.data || {}));
  };
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const owners = useMemo(() => {
    const m = {};
    leads.forEach((l) => { if (l.owner_id) m[l.owner_id] = l.owner_name; });
    return Object.entries(m).map(([id, name]) => ({ id, name }));
  }, [leads]);

  const filtered = useMemo(() => leads.filter((l) =>
    (fOwner === NONE || l.owner_id === fOwner) &&
    (fSource === NONE || l.lead_source === fSource) &&
    (!q || (l.company || "").toLowerCase().includes(q.toLowerCase()))
  ), [leads, fOwner, fSource, q]);

  const cols = useMemo(() => {
    const opens = (meta.open_stages || []).map((s, i, arr) => ({
      key: s.key, label: s.label, tone: OPEN_TONES[i % OPEN_TONES.length], cardTone: CARD_TONES[i % CARD_TONES.length],
      head: SALES_RAMP[Math.round((i / Math.max(1, arr.length - 1)) * (SALES_RAMP.length - 1))],
      headLight: i < Math.ceil(arr.length / 3),
    }));
    return [...opens,
      { key: "won", label: "Won", tone: "text-emerald-700 border-emerald-300", cardTone: "border-l-emerald-400", head: "#059669", headLight: false },
      { key: "lost", label: "Lost", tone: "text-red-700 border-red-300", cardTone: "border-l-red-400", head: "#DC2626", headLight: false },
      { key: "nurture", label: "Nurture", tone: "text-indigo-700 border-indigo-300", cardTone: "border-l-indigo-400", head: "#6366F1", headLight: false }];
  }, [meta]);

  const byStage = (s) => filtered.filter((l) => l.stage === s);

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAllBoard = () => setSelected(new Set(filtered.map((l) => l.id)));
  const selectAllStage = (key) => setSelected((s) => { const n = new Set(s); byStage(key).forEach((l) => n.add(l.id)); return n; });
  const clearSelection = () => setSelected(new Set());
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };
  const plural = (n) => `${n} opportunit${n === 1 ? "y" : "ies"}`;

  const selectByName = () => {
    const kw = matchKw.trim().toLowerCase();
    if (!kw) { toast.error("Type a name keyword first"); return; }
    const matches = filtered.filter((l) => (l.company || "").toLowerCase().includes(kw));
    if (!matches.length) { toast.info("No visible opportunities match that name"); return; }
    setSelected((s) => { const n = new Set(s); matches.forEach((l) => n.add(l.id)); return n; });
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    const closed = leads.filter((l) => selected.has(l.id) && (l.stage === "won" || l.stage === "lost")).length;
    const warn = closed ? ` ${closed === 1 ? "1 of these is a closed Won/Lost deal — deleting it" : `${closed} of these are closed Won/Lost deals — deleting them`} loses that history.` : "";
    const ok = await confirmDialog({ title: `Delete ${plural(ids.length)}?`, description: `This permanently removes the selected opportunities and any prospect businesses auto-created for them.${warn} You can undo this until you leave the page.`, confirmText: `Delete ${ids.length}`, tone: "destructive" });
    if (!ok) return;
    try {
      const { data } = await api.post("/pipeline/leads/bulk-delete", { ids });
      toast.success(`Deleted ${plural(data.deleted)}`);
      setLastDelete(data.undo_id ? { undo_id: data.undo_id, count: data.deleted } : null);
      exitSelect(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const emptyStage = async (col) => {
    const total = leads.filter((l) => l.stage === col.key).length;
    if (!total) { toast.info("This stage is already empty"); return; }
    const closed = col.key === "won" || col.key === "lost";
    const warn = closed ? ` These are closed ${col.label} deals — deleting them loses that history.` : "";
    const qty = total === 1 ? "the 1 opportunity" : `all ${total} opportunities`;
    const ok = await confirmDialog({ title: `Empty "${col.label}"?`, description: `This permanently deletes ${qty} in this stage — regardless of the current filters — and any prospect businesses auto-created for them.${warn} You can undo this until you leave the page.`, confirmText: `Delete ${total}`, tone: "destructive" });
    if (!ok) return;
    try {
      const { data } = await api.post("/pipeline/leads/empty-stage", { stage: col.key });
      toast.success(`Deleted ${plural(data.deleted)}`);
      setLastDelete(data.undo_id ? { undo_id: data.undo_id, count: data.deleted } : null);
      exitSelect(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not empty stage"); }
  };

  const undoDelete = async () => {
    if (!lastDelete) return;
    try {
      const { data } = await api.post(`/pipeline/bulk-delete/${lastDelete.undo_id}/restore`);
      toast.success(`Restored ${plural(data.restored)}`);
      setLastDelete(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not undo"); }
  };

  const svcById = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s])), [services]);
  const svcByCat = useMemo(() => {
    const m = {};
    services.forEach((s) => { (m[s.category || "Other"] ||= []).push(s); });
    return m;
  }, [services]);

  const openNew = () => {
    setForm({ ...emptyForm, stage: cols[0]?.key || "new_enquiry", owner_id: user?.id || "", owner_name: user?.name || "", enquiry_date: new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }).replace(/ /g, "-") });
    setEditingId(null); setSheetOpen(true);
  };
  const openEdit = (lead) => {
    const f = { ...emptyForm, ...lead, discovery: lead.discovery || {}, services_interested: lead.services_interested || [], note: "" };
    Object.keys(emptyForm).forEach((k) => { if (f[k] == null) f[k] = emptyForm[k]; });
    setForm(f); setEditingId(lead.id); setSheetOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) { await api.put(`/pipeline/leads/${editingId}`, form); toast.success("Opportunity updated"); }
      else { await api.post("/pipeline/leads", form); toast.success("Prospect added"); }
      setSheetOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!editingId) return;
    const ok = await confirmDialog({ title: "Delete opportunity?", description: "This removes the opportunity. The linked business record is kept.", confirmText: "Delete", tone: "destructive" });
    if (!ok) return;
    await api.delete(`/pipeline/leads/${editingId}`);
    toast.success("Deleted"); setSheetOpen(false); load();
  };

  const addNote = async () => {
    const text = noteInput.trim();
    if (!text || !editingId) return;
    try {
      const { data } = await api.post(`/pipeline/leads/${editingId}/notes`, { text });
      setForm((f) => ({ ...f, notes: data.notes }));
      setLeads((ls) => ls.map((l) => (l.id === editingId ? { ...l, notes: data.notes } : l)));
      setNoteInput(""); toast.success("Note added");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add note"); }
  };

  const parse = async (textArg) => {
    const text = (textArg ?? form.note ?? "").trim();
    if (!text) { toast.error("Type or dictate a note first"); return; }
    setParsing(true);
    try {
      const { data } = await api.post("/pipeline/parse-note", { text });
      setForm((f) => ({ ...f,
        company: data.company || f.company, contact_name: data.contact_name || f.contact_name,
        email: data.email || f.email, phone: data.phone || f.phone,
        expected_fee: data.expected_fee || f.expected_fee, close_date: data.close_date || f.close_date }));
      toast.success("Note parsed — review the fields");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not parse the note"); }
    finally { setParsing(false); }
  };

  const moveStage = async (id, stage) => {
    if (stage === "won" || stage === "lost") { setReason({ open: true, id, stage, value: "", detail: "" }); return; }
    try { await api.post(`/pipeline/leads/${id}/stage`, { stage }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not move"); }
  };

  const confirmReason = async () => {
    if (!reason.value) { toast.error("Pick a reason"); return; }
    try {
      await api.post(`/pipeline/leads/${reason.id}/stage`, { stage: reason.stage, reason: reason.value, detail: reason.detail });
      toast.success(reason.stage === "won" ? "Marked Won" : "Marked Lost");
      setReason({ open: false, id: null, stage: null, value: "", detail: "" });
      if (sheetOpen) setSheetOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };

  const logTouchpoint = async (tp, outcome) => {
    if (!editingId) return;
    try {
      const { data } = await api.post(`/pipeline/leads/${editingId}/touchpoint`, { title: tp.title, outcome });
      setForm((f) => ({ ...f, activity: data.activity }));
      setLeads((ls) => ls.map((l) => (l.id === editingId ? { ...l, activity: data.activity } : l)));
      toast.success("Touchpoint logged");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not log touchpoint"); }
  };

  const createProposal = async () => {    if (!editingId) return;
    if (editingId && (form.company !== undefined)) {
      try { await api.put(`/pipeline/leads/${editingId}`, form); } catch { /* keep going */ }
    }
    try {
      const { data } = await api.post(`/pipeline/leads/${editingId}/convert`);
      toast.success(`Proposal ${data.reference} created — services pre-filled`);
      navigate(`/proposals/${data.proposal_id}/edit`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create proposal"); }
  };

  const onDragEnd = ({ active, over }) => {
    if (!over) return;
    const lead = leads.find((l) => l.id === active.id);
    if (lead && lead.stage !== over.id) moveStage(active.id, over.id);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDisc = (k, v) => setForm((f) => ({ ...f, discovery: { ...(f.discovery || {}), [k]: v } }));
  const toggleService = (id) => setForm((f) => {
    const cur = f.services_interested || [];
    return { ...f, services_interested: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });
  const reasonList = reason.stage === "won" ? (meta.win_reasons || []) : (meta.loss_reasons || []);
  const d = form.discovery || {};

  const timeline = useMemo(() => {
    const items = [
      ...(form.activity || []).map((a) => ({ ...a, kind: "activity" })),
      ...(form.notes || []).map((n) => ({ ...n, kind: "note" })),
    ];
    return items.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  }, [form.activity, form.notes]);

  return (
    <div className="p-6 md:p-8" data-testid="pipeline-page">
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h1 className="font-alt text-3xl font-bold text-cass-navy">Sales Pipeline</h1>
        <Button onClick={openNew} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="pipeline-add-lead-btn"><Plus className="w-4 h-4 mr-2" />Add Prospect</Button>
        {isAdmin && (
          <Button variant="outline" onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} className={selectMode ? "border-fuchsia-400 text-fuchsia-700" : ""} data-testid="pipeline-select-toggle">
            <CheckSquare className="w-4 h-4 mr-2" />{selectMode ? "Done" : "Select"}
          </Button>
        )}
      </div>
      <p className="text-cass-slate text-sm mb-5">One journey from first enquiry to a signed proposal. Drag a card to change its stage.</p>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <Select value={fOwner} onValueChange={setFOwner}>
          <SelectTrigger className="w-44" data-testid="filter-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
          <SelectContent><SelectItem value={NONE}>All owners</SelectItem>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fSource} onValueChange={setFSource}>
          <SelectTrigger className="w-44" data-testid="filter-source"><SelectValue placeholder="Lead source" /></SelectTrigger>
          <SelectContent><SelectItem value={NONE}>All sources</SelectItem>{(meta.lead_sources || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search business…" className="w-56" data-testid="filter-search" />
      </div>

      {selectMode && (
        <div className="mb-5 p-2.5 rounded-lg bg-fuchsia-50 border border-fuchsia-200 space-y-2" data-testid="pipeline-bulk-bar">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-cass-navy" data-testid="pipeline-selected-count">{selected.size} selected</span>
            <Button size="sm" variant="outline" onClick={selectAllBoard} data-testid="pipeline-select-all-board">Select all ({filtered.length})</Button>
            <Button size="sm" variant="outline" onClick={clearSelection} disabled={!selected.size} data-testid="pipeline-clear-selection">Clear selection</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={deleteSelected} disabled={!selected.size} data-testid="pipeline-delete-selected"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete selected</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t border-fuchsia-200 pt-2">
            <span className="text-xs font-medium text-cass-slate">Quick select:</span>
            <Button size="sm" variant="outline" onClick={() => selectAllStage("lost")} data-testid="pipeline-quick-lost">Only Lost</Button>
            <Button size="sm" variant="outline" onClick={() => selectAllStage("nurture")} data-testid="pipeline-quick-nurture">Only Nurture</Button>
            <Input value={matchKw} onChange={(e) => setMatchKw(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); selectByName(); } }} placeholder="Name contains… e.g. TEST" className="w-48 h-8" data-testid="pipeline-quick-kw" />
            <Button size="sm" variant="outline" onClick={selectByName} data-testid="pipeline-quick-match">Match name</Button>
            <span className="text-xs text-cass-slate ml-auto">Selection &amp; quick select respect your current filters. “Empty stage” clears the whole stage.</span>
          </div>
        </div>
      )}

      {lastDelete && !selectMode && (
        <div className="flex items-center gap-3 mb-5 p-2.5 rounded-lg bg-amber-50 border border-amber-200" data-testid="pipeline-undo-bar">
          <span className="text-sm text-cass-navy">Deleted {plural(lastDelete.count)}.</span>
          <Button size="sm" variant="outline" onClick={undoDelete} data-testid="pipeline-undo-btn"><ArrowUp className="w-3.5 h-3.5 mr-1 rotate-[-45deg]" />Undo</Button>
          <button onClick={() => setLastDelete(null)} className="ml-auto text-xs text-cass-slate hover:text-cass-navy" data-testid="pipeline-undo-dismiss">Dismiss</button>
        </div>
      )}

      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
          {cols.map((c) => <Column key={c.key} col={c} leads={byStage(c.key)} onOpen={openEdit} isFocus={c.key === focusStage} selectMode={selectMode} selected={selected} onToggleSelect={toggleSelect} onSelectAllStage={selectAllStage} onEmptyStage={emptyStage} isAdmin={isAdmin} stageTotal={leads.filter((l) => l.stage === c.key).length} />)}
        </div>
      </DndContext>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="lead-sheet">
          <SheetHeader><SheetTitle>{editingId ? form.company || "Opportunity" : "Add Prospect"}</SheetTitle>
            <SheetDescription>{editingId ? "Manage this opportunity across its journey to a signed proposal." : "Quickly capture a new enquiry — most fields are optional."}</SheetDescription></SheetHeader>

          <div className="flex flex-wrap gap-1 mt-4">
            {cols.map((c) => (
              <button key={c.key} onClick={() => set("stage", c.key)} data-testid={`lead-stage-${c.key}`}
                className={`text-xs px-2.5 py-1 rounded-full border ${form.stage === c.key ? "bg-fuchsia-600 text-white border-fuchsia-600" : "border-border text-cass-slate hover:border-fuchsia-400"}`}>{c.label}</button>
            ))}
          </div>

          <Tabs defaultValue="overview" className="mt-4">
            <TabsList className="grid grid-cols-6 w-full">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="discovery" data-testid="tab-discovery">Discovery</TabsTrigger>
              <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
              <TabsTrigger value="touchpoints" data-testid="tab-touchpoints">Touch</TabsTrigger>
              <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
              <TabsTrigger value="proposals" data-testid="tab-proposals">Proposals</TabsTrigger>
            </TabsList>

            {/* OVERVIEW */}
            <TabsContent value="overview" className="space-y-3">
              {!editingId && (
                <div className="bg-cass-bone rounded-lg p-3">
                  <label className="text-xs font-semibold text-cass-navy flex items-center gap-1 mb-1"><Sparkles className="w-3.5 h-3.5 text-cass-teal" />Quick capture</label>
                  <Textarea value={form.note} onChange={(e) => set("note", e.target.value)} rows={3} placeholder="e.g. Spoke to Acme, wants monthly bookkeeping ~£800/mo, came via referral, decision by end of June" data-testid="lead-note-input" />
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Button size="sm" variant="outline" onClick={() => parse()} disabled={parsing} data-testid="lead-parse-btn"><Sparkles className="w-3.5 h-3.5 mr-1" />{parsing ? "Reading…" : "Auto-fill from note"}</Button>
                    <MicButton testid="lead-note-mic" onText={(t) => { const merged = (form.note ? form.note + " " : "") + t; set("note", merged); parse(merged); }} />
                    <AudioUploadButton testid="lead-note-upload" onText={(t) => { const merged = (form.note ? form.note + " " : "") + t; set("note", merged); parse(merged); }} />
                  </div>
                </div>
              )}
              <Field label="Business / prospect">
                <Input list="pl-clients" value={form.company} onChange={(e) => {
                  const v = e.target.value; const c = clients.find((x) => (x.trading_name || x.legal_name) === v);
                  setForm((f) => ({ ...f, company: v, client_id: c ? c.id : "", contact_name: c && !f.contact_name ? ((c.contacts || [])[0]?.name || f.contact_name) : f.contact_name }));
                }} data-testid="lead-field-company" />
                <datalist id="pl-clients">{clients.map((c) => <option key={c.id} value={c.trading_name || c.legal_name} />)}</datalist>
              </Field>
              <Field label="Contact name"><Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="lead-field-contact" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lead-field-email" /></Field>
                <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lead-field-phone" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Website"><Input value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="lead-field-website" /></Field>
                <Field label="Company number"><Input value={form.company_number} onChange={(e) => set("company_number", e.target.value)} data-testid="lead-field-company-number" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Business type">
                  <Select value={form.business_type || NONE} onValueChange={(v) => set("business_type", v === NONE ? "" : v)}>
                    <SelectTrigger data-testid="lead-field-business-type"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent><SelectItem value={NONE}>—</SelectItem>{BUSINESS_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Date enquiry received"><Input value={form.enquiry_date} onChange={(e) => set("enquiry_date", e.target.value)} placeholder="06-Sep-26" data-testid="lead-field-enquiry-date" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Lead source">
                  <Select value={form.lead_source || NONE} onValueChange={(v) => set("lead_source", v === NONE ? "" : v)}>
                    <SelectTrigger data-testid="lead-field-lead-source"><SelectValue placeholder="How did they find us?" /></SelectTrigger>
                    <SelectContent><SelectItem value={NONE}>—</SelectItem>{(meta.lead_sources || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Referrer">
                  <Input list="pl-referrers" value={form.referrer} onChange={(e) => {
                    const v = e.target.value; const c = clients.find((x) => (x.trading_name || x.legal_name) === v);
                    setForm((f) => ({ ...f, referrer: v, referrer_client_id: c ? c.id : "" }));
                  }} placeholder="Who referred them?" data-testid="lead-field-referrer" />
                  <datalist id="pl-referrers">{clients.map((c) => <option key={c.id} value={c.trading_name || c.legal_name} />)}</datalist>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Estimated value (£/mo)"><Input type="number" value={form.expected_fee} onChange={(e) => set("expected_fee", e.target.value)} data-testid="lead-field-fee" /></Field>
                <Field label="Owner">
                  <Select value={form.owner_id || NONE} onValueChange={(v) => { const u = users.find((x) => x.id === v); setForm((f) => ({ ...f, owner_id: v === NONE ? "" : v, owner_name: u ? u.name : f.owner_name })); }}>
                    <SelectTrigger data-testid="lead-field-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
                    <SelectContent>
                      {users.length === 0 && form.owner_id && <SelectItem value={form.owner_id}>{form.owner_name}</SelectItem>}
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Lead temperature">
                <div className="flex gap-2" data-testid="lead-field-temperature">
                  {Object.entries(TEMPS).map(([k, t]) => (
                    <button key={k} type="button" onClick={() => set("temperature", k)} data-testid={`lead-temp-opt-${k}`}
                      className={`flex-1 inline-flex items-center justify-center gap-1 text-xs font-medium px-2 py-1.5 rounded-md border transition-colors ${(form.temperature || "warm") === k ? t.pill : "border-border text-cass-slate hover:border-fuchsia-300"}`}>
                      <Flame className={`w-3.5 h-3.5 ${(form.temperature || "warm") === k ? t.flame : ""}`} />{t.label}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Reason for enquiry"><Textarea rows={2} value={form.reason_for_enquiry} onChange={(e) => set("reason_for_enquiry", e.target.value)} data-testid="lead-field-reason" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Current accountant"><Input value={form.current_accountant} onChange={(e) => set("current_accountant", e.target.value)} data-testid="lead-field-accountant" /></Field>
                <Field label="Current software"><Input value={form.current_software} onChange={(e) => set("current_software", e.target.value)} data-testid="lead-field-software" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Turnover band">
                  <Select value={form.turnover_band || NONE} onValueChange={(v) => set("turnover_band", v === NONE ? "" : v)}>
                    <SelectTrigger data-testid="lead-field-turnover"><SelectValue placeholder="Approx turnover" /></SelectTrigger>
                    <SelectContent><SelectItem value={NONE}>—</SelectItem>{TURNOVER_BANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Employees"><Input value={form.employee_count} onChange={(e) => set("employee_count", e.target.value)} data-testid="lead-field-employees" /></Field>
              </div>
              <div className="border-t border-border pt-3">
                <div className="text-xs font-semibold text-cass-navy mb-2">Next action</div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="What next?"><Input value={form.next_action} onChange={(e) => set("next_action", e.target.value)} placeholder="e.g. Call prospect" data-testid="lead-field-next-action" /></Field>
                  <Field label="By when"><Input value={form.next_action_date} onChange={(e) => set("next_action_date", e.target.value)} placeholder="10-Sep-26" data-testid="lead-field-next-action-date" /></Field>
                </div>
              </div>
              {form.stage === "nurture" && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                  <Field label="Recontact date (resurfaces when due)"><Input value={form.recontact_date || ""} onChange={(e) => set("recontact_date", e.target.value)} placeholder="01-Dec-26" data-testid="lead-field-recontact-date" /></Field>
                </div>
              )}
            </TabsContent>

            {/* DISCOVERY */}
            <TabsContent value="discovery" className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <Field label="Discovery status">
                  <Select value={d.status || NONE} onValueChange={(v) => setDisc("status", v === NONE ? "" : v)}>
                    <SelectTrigger data-testid="disc-status"><SelectValue placeholder="Not booked" /></SelectTrigger>
                    <SelectContent><SelectItem value={NONE}>Not booked</SelectItem>{DISCOVERY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Meeting type">
                  <Select value={d.meeting_type || NONE} onValueChange={(v) => setDisc("meeting_type", v === NONE ? "" : v)}>
                    <SelectTrigger data-testid="disc-meeting-type"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent><SelectItem value={NONE}>—</SelectItem>{MEETING_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Date"><Input value={d.date || ""} onChange={(e) => setDisc("date", e.target.value)} placeholder="12-Sep-26" data-testid="disc-date" /></Field>
                <Field label="Time"><Input value={d.time || ""} onChange={(e) => setDisc("time", e.target.value)} placeholder="14:00" data-testid="disc-time" /></Field>
              </div>
              <p className="text-xs font-semibold text-cass-navy pt-1">Current situation</p>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Bookkeeping"><Input value={d.bookkeeping || ""} onChange={(e) => setDisc("bookkeeping", e.target.value)} data-testid="disc-bookkeeping" /></Field>
                <Field label="Payroll"><Input value={d.payroll || ""} onChange={(e) => setDisc("payroll", e.target.value)} data-testid="disc-payroll" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="VAT"><Input value={d.vat || ""} onChange={(e) => setDisc("vat", e.target.value)} data-testid="disc-vat" /></Field>
                <Field label="Reporting / finance resource"><Input value={d.reporting || ""} onChange={(e) => setDisc("reporting", e.target.value)} data-testid="disc-reporting" /></Field>
              </div>
              <Field label="Why are they looking?"><Textarea rows={2} value={d.why_looking || ""} onChange={(e) => setDisc("why_looking", e.target.value)} data-testid="disc-why" /></Field>
              <Field label="Current problems"><Textarea rows={2} value={d.problems || ""} onChange={(e) => setDisc("problems", e.target.value)} data-testid="disc-problems" /></Field>
              <Field label="Goals"><Textarea rows={2} value={d.goals || ""} onChange={(e) => setDisc("goals", e.target.value)} data-testid="disc-goals" /></Field>
              <p className="text-xs font-semibold text-cass-navy pt-1">Timing</p>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Desired start date"><Input value={d.start_date || ""} onChange={(e) => setDisc("start_date", e.target.value)} placeholder="01-Oct-26" data-testid="disc-start" /></Field>
                <Field label="Year end"><Input value={d.year_end || ""} onChange={(e) => setDisc("year_end", e.target.value)} placeholder="31-Mar" data-testid="disc-year-end" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="VAT quarter"><Input value={d.vat_quarter || ""} onChange={(e) => setDisc("vat_quarter", e.target.value)} data-testid="disc-vat-quarter" /></Field>
                <Field label="Payroll frequency"><Input value={d.payroll_frequency || ""} onChange={(e) => setDisc("payroll_frequency", e.target.value)} data-testid="disc-payroll-freq" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Urgency">
                  <Select value={d.urgency || NONE} onValueChange={(v) => setDisc("urgency", v === NONE ? "" : v)}>
                    <SelectTrigger data-testid="disc-urgency"><SelectValue placeholder="Urgency" /></SelectTrigger>
                    <SelectContent><SelectItem value={NONE}>—</SelectItem>{URGENCY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Notice period"><Input value={d.notice_period || ""} onChange={(e) => setDisc("notice_period", e.target.value)} data-testid="disc-notice" /></Field>
              </div>
              <Field label="Decision makers"><Input value={d.decision_makers || ""} onChange={(e) => setDisc("decision_makers", e.target.value)} data-testid="disc-decision-makers" /></Field>
              <Field label="Discovery outcome">
                <Select value={d.outcome || NONE} onValueChange={(v) => setDisc("outcome", v === NONE ? "" : v)}>
                  <SelectTrigger data-testid="disc-outcome"><SelectValue placeholder="Outcome" /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE}>—</SelectItem>{(meta.discovery_outcomes || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <p className="text-[11px] text-cass-slate">Setting a status or outcome moves the stage automatically (Booked → Discovery Booked, Completed → Discovery Completed, Ready for Proposal → Ready for Proposal). Save to apply.</p>
              <div className="bg-slate-50 border border-border rounded-lg p-3" data-testid="discovery-brief">
                <div className="text-xs font-semibold text-cass-navy mb-1">Discovery brief</div>
                <div className="text-xs text-cass-slate space-y-0.5">
                  <div><b>{form.company || "—"}</b> · {form.contact_name || "no contact"}{d.meeting_type ? ` · ${d.meeting_type}` : ""}{d.date ? ` · ${d.date}${d.time ? " " + d.time : ""}` : ""}</div>
                  {d.why_looking && <div>Why: {d.why_looking}</div>}
                  {d.problems && <div>Problems: {d.problems}</div>}
                  {d.goals && <div>Goals: {d.goals}</div>}
                  {(form.services_interested || []).length > 0 && <div>Services: {(form.services_interested || []).map((id) => svcById[id]?.client_name).filter(Boolean).join(", ")}</div>}
                  {d.start_date && <div>Wants to start: {d.start_date}</div>}
                </div>
              </div>
            </TabsContent>

            {/* SERVICES */}
            <TabsContent value="services" className="space-y-3">
              <p className="text-xs text-cass-slate">Tick the services of interest from the catalogue. These pre-fill the proposal when you create it.</p>
              {Object.entries(svcByCat).map(([cat, items]) => (
                <div key={cat}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-cass-slate/70 mb-1">{cat}</div>
                  <div className="space-y-1">
                    {items.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`svc-interest-${s.internal_name}`}>
                        <Checkbox checked={(form.services_interested || []).includes(s.id)} onCheckedChange={() => toggleService(s.id)} />
                        <span className="text-cass-navy">{s.client_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {services.length === 0 && <p className="text-sm text-cass-slate">No services in the catalogue.</p>}
            </TabsContent>

            {/* TOUCHPOINTS */}
            <TabsContent value="touchpoints" className="space-y-3">
              <p className="text-xs text-cass-slate">Suggested actions for this opportunity's current stage. Log one to record it in the activity timeline. Automated sending stays off — CASS keeps control of client contact.</p>
              {editingId ? (
                <>
                  {(meta.touchpoints || []).filter((t) => t.stage === form.stage).map((t) => (
                    <div key={t.id} className="border border-border rounded-lg p-3" data-testid={`touchpoint-${t.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-cass-navy text-sm">{t.title}</div>
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className={`text-[10px] ${t.type === "manual" ? "border-slate-300 text-cass-slate" : "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200"}`}>{t.type}</Badge>
                          {Number(t.timing_days) > 0 && <span className="text-[10px] text-cass-slate">+{t.timing_days}d</span>}
                        </div>
                      </div>
                      {t.description && <div className="text-xs text-cass-slate mt-1">{t.description}</div>}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(t.outcomes || []).map((o) => (
                          <button key={o} onClick={() => logTouchpoint(t, o)} data-testid={`touchpoint-log-${t.id}-${o}`}
                            className="text-xs px-2.5 py-1 rounded-full border border-border text-cass-slate hover:border-fuchsia-400 hover:text-fuchsia-600 transition-colors">{o}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {(meta.touchpoints || []).filter((t) => t.stage === form.stage).length === 0 &&
                    <p className="text-sm text-cass-slate">No touchpoints configured for this stage. Use the <b>Touchpoints</b> button in the header to add some.</p>}
                </>
              ) : <p className="text-sm text-cass-slate">Touchpoints appear once the prospect is saved.</p>}
            </TabsContent>

            {/* ACTIVITY */}
            <TabsContent value="activity" className="space-y-3">
              {editingId ? (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input value={noteInput} onChange={(e) => setNoteInput(e.target.value)} placeholder="Add a note…" data-testid="lead-add-note-input" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(); } }} />
                    <MicButton testid="lead-note-mic-edit" onText={(t) => setNoteInput((v) => (v ? v + " " : "") + t)} />
                    <Button size="sm" variant="outline" onClick={addNote} data-testid="lead-add-note-btn">Add</Button>
                  </div>
                  <div className="space-y-1.5 max-h-80 overflow-y-auto">
                    {timeline.length === 0 && <p className="text-sm text-cass-slate">No activity yet.</p>}
                    {timeline.map((it, i) => (
                      <div key={i} className="text-xs border-l-2 border-border pl-2 py-0.5" data-testid={`activity-row-${i}`}>
                        <span className="text-slate-400">{it.by || "—"} · {fmtDT(it.at)}</span>
                        {it.kind === "activity" ? <span className="text-cass-navy"> · {it.text}</span> : <span className="text-cass-slate"> · {it.text}</span>}
                      </div>
                    ))}
                  </div>
                </>
              ) : <p className="text-sm text-cass-slate">Activity appears once the prospect is saved.</p>}
            </TabsContent>

            {/* PROPOSALS */}
            <TabsContent value="proposals" className="space-y-3">
              {form.stage === "ready_for_proposal" && !form.proposal_id && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                  <p className="text-sm text-cass-navy mb-2">This opportunity is ready. Create a proposal with the business, contact, services of interest and start date pre-filled.</p>
                  <Button onClick={createProposal} className="bg-cass-teal hover:bg-teal-600 text-white" data-testid="lead-create-proposal-btn"><ArrowRight className="w-4 h-4 mr-1" />Create proposal</Button>
                </div>
              )}
              {form.proposal_id ? (
                <div className="flex items-center justify-between border border-border rounded-lg p-3">
                  <div className="text-sm text-cass-navy">Linked proposal</div>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/proposals/${form.proposal_id}`)} data-testid="lead-view-proposal-btn"><ExternalLink className="w-4 h-4 mr-1" />View proposal</Button>
                </div>
              ) : form.stage !== "ready_for_proposal" && (
                <p className="text-sm text-cass-slate">No proposal yet. Move to <b>Ready for Proposal</b> (or complete Discovery with the “Ready for Proposal” outcome) to create one.</p>
              )}
              {editingId && (
                <Button size="sm" variant="outline" onClick={createProposal} data-testid="lead-convert-btn"><ArrowRight className="w-4 h-4 mr-1" />Create proposal now</Button>
              )}
            </TabsContent>
          </Tabs>

          <div className="flex flex-wrap items-center gap-2 pt-3 mt-3 border-t border-border">
            <Button onClick={save} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="lead-save-btn">{saving ? "Saving…" : "Save"}</Button>
            {editingId && (
              <>
                <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300" onClick={() => setReason({ open: true, id: editingId, stage: "won", value: "", detail: "" })} data-testid="lead-mark-won-btn"><Check className="w-3.5 h-3.5 mr-1" />Won</Button>
                <Button size="sm" variant="outline" className="text-red-700 border-red-300" onClick={() => setReason({ open: true, id: editingId, stage: "lost", value: "", detail: "" })} data-testid="lead-mark-lost-btn"><X className="w-3.5 h-3.5 mr-1" />Lost</Button>
                <button onClick={del} className="ml-auto text-red-500 hover:text-red-700" title="Delete opportunity" data-testid="lead-delete-btn"><Trash2 className="w-4 h-4" /></button>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={reason.open} onOpenChange={(o) => setReason((r) => ({ ...r, open: o }))}>
        <DialogContent data-testid="reason-dialog">
          <DialogHeader><DialogTitle>{reason.stage === "won" ? "Mark as Won" : "Mark as Lost"}</DialogTitle>
            <DialogDescription>Record why this opportunity was {reason.stage === "won" ? "won" : "lost"}.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-cass-slate">Reason</label>
              <Select value={reason.value} onValueChange={(v) => setReason((r) => ({ ...r, value: v }))}>
                <SelectTrigger data-testid="reason-select"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
                <SelectContent>{reasonList.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-cass-slate">Details (optional)</label>
              <Textarea value={reason.detail} onChange={(e) => setReason((r) => ({ ...r, detail: e.target.value }))} rows={2} data-testid="reason-detail" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReason((r) => ({ ...r, open: false }))}>Cancel</Button>
            <Button className="bg-cass-navy text-white" onClick={confirmReason} data-testid="reason-confirm-btn">{reason.stage === "won" ? "Mark Won" : "Mark Lost"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><label className="text-xs text-cass-slate block mb-1">{label}</label>{children}</div>);
}
